"use strict";
const debounce = (fn, delay) => {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
};

const buildUrl = (base, params) => {
  const url = new URL(base, window.location.origin);
  for (const [k, v] of params) {
    if (v) url.searchParams.append(k, v);
  }
  return url.toString();
};

// ─── ab-filter-price-range ────────────────────────────────────────
class AbFilterPriceRange extends HTMLElement {
  connectedCallback() {
    this.min = parseFloat(this.dataset.min);
    this.max = parseFloat(this.dataset.max);
    this.initialMin = parseFloat(this.dataset.collectionMin);

    this.pMin = this.dataset.paramMin;
    this.pMax = this.dataset.paramMax;

    this.vMin = parseFloat(this.dataset.minValue);
    this.vMax = parseFloat(this.dataset.maxValue);

    this.track = this.querySelector(".ab-filter-range-track");
    this.active = this.querySelector(".ab-cf-range-active");
    this.hMin = this.querySelector(".ab-cf-range-handle--min");
    this.hMax = this.querySelector(".ab-cf-range-handle--max");

    this.iMin = this.querySelector("#ab-price-min-input");
    this.iMax = this.querySelector("#ab-price-max-input");

    this.nMin = this.querySelector("#ab-price-min-number");
    this.nMax = this.querySelector("#ab-price-max-number");

    this.userChanged = false;

    this.render();
    this.syncNames();
    this.attachDrag();

    this.style.visibility = "visible";

    // Number inputs
    this.nMin.addEventListener("change", () => {
      this.userChanged = true;

      this.vMin = Math.max(
        this.min,
        Math.min(+this.nMin.value || this.min, this.vMax - 0.01),
      );

      this.vMin = this.round(this.vMin);

      this.render();
      this.syncNames();
      this.emit();
    });

    this.nMax.addEventListener("change", () => {
      this.userChanged = true;

      this.vMax = Math.min(
        this.max,
        Math.max(+this.nMax.value || this.max, this.vMin + 0.01),
      );

      this.vMax = this.round(this.vMax);

      this.render();
      this.syncNames();
      this.emit();
    });

    // Track click
    this.track.addEventListener("click", (e) => {
      if (e.target === this.hMin || e.target === this.hMax) return;

      this.userChanged = true;

      const rect = this.track.getBoundingClientRect();
      const val = this.round(this.eventToValue(e, rect));

      if (Math.abs(val - this.vMin) <= Math.abs(val - this.vMax)) {
        this.vMin = Math.max(this.min, Math.min(val, this.vMax - 0.01));
      } else {
        this.vMax = Math.min(this.max, Math.max(val, this.vMin + 0.01));
      }

      this.render();
      this.syncNames();
      this.emit();
    });
  }

  render() {
    const range = this.max - this.min || 1;

    const pMin = ((this.vMin - this.min) / range) * 100;
    const pMax = ((this.vMax - this.min) / range) * 100;

    const trackW = this.track.getBoundingClientRect().width;

    if (!trackW) return; // safety

    const minPx = Math.min(Math.max((pMin / 100) * trackW, 10), trackW - 10);
    const maxPx = Math.min(Math.max((pMax / 100) * trackW, 10), trackW - 10);

    this.hMin.style.left = `${minPx}px`;
    this.hMax.style.left = `${maxPx}px`;

    this.active.style.left = `${pMin}%`;
    this.active.style.width = `${pMax - pMin}%`;

    this.hMin.style.zIndex = pMin > 50 ? 6 : 4;
    this.hMax.style.zIndex = pMax > 50 ? 4 : 6;

    if (this.nMin) this.nMin.value = this.vMin;
    if (this.nMax) this.nMax.value = this.vMax;
    if (this.iMin) this.iMin.value = this.vMin;
    if (this.iMax) this.iMax.value = this.vMax;
  }

  syncNames() {
    // detect reset state
    const isAtDefault =
      Math.abs(this.vMin - this.min) < 0.01 &&
      Math.abs(this.vMax - this.max) < 0.01;

    if (isAtDefault) {
      this.userChanged = false;
    }

    if (this.iMin) this.iMin.name = this.userChanged ? this.pMin : "";
    if (this.iMax) this.iMax.name = this.userChanged ? this.pMax : "";
  }

  attachDrag() {
    let dragging = null;
    let rect = null;

    const onMove = (e) => {
      if (!dragging) return;
      if (e.cancelable) e.preventDefault();

      this.userChanged = true;

      const val = this.eventToValue(e, rect);

      if (dragging === "min") {
        this.vMin = Math.max(this.min, Math.min(val, this.vMax - 0.01));
        this.vMin = this.round(this.vMin);
      } else {
        this.vMax = Math.min(this.max, Math.max(val, this.vMin + 0.01));
        this.vMax = this.round(this.vMax);
      }

      this.render();
      this.syncNames();
      this.emit();
    };

    const onUp = () => {
      if (!dragging) return;

      (dragging === "min" ? this.hMin : this.hMax).classList.remove(
        "is-dragging",
      );
      dragging = null;
    };

    const startDrag = (which) => (e) => {
      e.preventDefault();

      dragging = which;
      rect = this.track.getBoundingClientRect();

      this.hMin.style.zIndex = which === "min" ? 6 : 4;
      this.hMax.style.zIndex = which === "max" ? 4 : 6;

      (which === "min" ? this.hMin : this.hMax).classList.add("is-dragging");
    };

    this.hMin.addEventListener("pointerdown", startDrag("min"));
    this.hMax.addEventListener("pointerdown", startDrag("max"));

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  eventToValue(e, rect) {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    return this.min + ratio * (this.max - this.min);
  }

  emit() {
    this.dispatchEvent(
      new CustomEvent("price-range:change", { bubbles: true }),
    );
  }

  round(v) {
    return Math.round(v * 100) / 100;
  }

  updateRange(absMin, absMax) {
    // update inputs always
    if (this.nMin) {
      this.nMin.min = absMin;
      this.nMin.max = absMax;
    }

    if (this.nMax) {
      this.nMax.min = absMin;
      this.nMax.max = absMax;
    }

    // if user has not touched → full update
    if (!this.userChanged) {
      this.min = absMin;
      this.max = absMax;

      this.vMin = absMin;
      this.vMax = absMax;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.render();
          this.syncNames();
        });
      });

      return;
    }

    let changed = false;

    if (this.vMin < absMin) {
      this.vMin = absMin;
      changed = true;
    }

    if (this.vMax > absMax) {
      this.vMax = absMax;
      changed = true;
    }

    if (changed) {
      requestAnimationFrame(() => {
        this.render();
        this.syncNames();
      });
    }
  }

  reset() {
    this.userChanged = false;

    this.vMin = this.min;
    this.vMax = this.max;

    this.render();
    this.syncNames();
  }
}

customElements.define("ab-filter-price-range", AbFilterPriceRange);

// ─── ab-filter-group ─────────────────────────────────────────────
class AbFilterGroup extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector(".ab-filter-group__toggle");
    this.body = this.querySelector(".ab-filter-group__body");
    if (!this.btn) return;
    this.btn.addEventListener("click", () => this.toggle());
    const isOpen = this.classList.contains("is-open");
    this.btn.setAttribute("aria-expanded", String(isOpen));
    if (isOpen) this.body.style.maxHeight = `${this.body.scrollHeight + 32}px`;
  }

  toggle() {
    const wasOpen = this.btn.getAttribute("aria-expanded") === "true";
    this.btn.setAttribute("aria-expanded", String(!wasOpen));
    this.classList.toggle("is-open", !wasOpen);
    this.body.style.maxHeight = wasOpen
      ? "0"
      : `${this.body.scrollHeight + 32}px`;
  }

  refreshHeight() {
    if (this.classList.contains("is-open"))
      this.body.style.maxHeight = `${this.body.scrollHeight + 32}px`;
  }
}

customElements.define("ab-filter-group", AbFilterGroup);

// ─── ab-cf-sort ──────────────────────────────────────────────────
class AbCfSort extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector(".ab-cf-sort__trigger");
    this.textEl = this.querySelector(".ab-cf-sort__label");
    this.list = this.querySelector(".ab-cf-sort__options");
    this._isOpen = false;
    if (!this.trigger || !this.list) return;

    this.trigger.addEventListener("click", () => this.toggle());
    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) this.close();
    });
    this.list
      .querySelectorAll(".ab-cf-sort__option")
      .forEach((opt) => opt.addEventListener("click", () => this.pick(opt)));
  }

  toggle() {
    this._isOpen ? this.close() : this.open();
  }

  open() {
    this._isOpen = true;
    this.setAttribute("aria-expanded", "true");
    this.list.classList.add("is-open");
    this.list.querySelector(".is-selected")?.focus();
  }

  close() {
    this._isOpen = false;
    this.setAttribute("aria-expanded", "false");
    this.list.classList.remove("is-open");
  }

  pick(opt) {
    if (this.textEl) this.textEl.textContent = opt.textContent.trim();
    this.list.querySelectorAll(".ab-cf-sort__option").forEach((o) => {
      o.classList.toggle("is-selected", o === opt);
      o.setAttribute("aria-selected", String(o === opt));
    });
    this.close();
    this.dispatchEvent(
      new CustomEvent("ab-sort:change", {
        bubbles: true,
        detail: {
          value: opt.dataset.value,
          isDefault: opt.dataset.isDefault === "true",
        },
      }),
    );
  }

  silentSelect(value) {
    const opt = this.list?.querySelector(`[data-value="${value}"]`);
    if (!opt) return;
    if (this.textEl) this.textEl.textContent = opt.textContent.trim();
    this.list.querySelectorAll(".ab-cf-sort__option").forEach((o) => {
      o.classList.toggle("is-selected", o === opt);
      o.setAttribute("aria-selected", String(o === opt));
    });
  }
}

customElements.define("ab-cf-sort", AbCfSort);

// ─── ab-cf-filter-form ────────────────────────────────────────────
class AbCfFilterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("#ab-cf-filter-form");
    this.baseUrl = this.form.dataset.collectionUrl;
    this.sortVal =
      new URLSearchParams(window.location.search).get("sort_by") || "";

    this.fetchNow = debounce(() => this.doFetch(), 250);
    this.infiniteScrollEnabled = false;
    this.isLoading = false;
    this.observer = null;

    // Drawer
    this.sidebar = document.getElementById("ab-cf-sidebar");
    this.backdrop = document.getElementById("ab-cf-backdrop");
    this.openBtn = document.getElementById("ab-cf-filter-open");
    this.closeBtn = document.getElementById("ab-cf-filter-close");
    this.applyBtn = document.getElementById("ab-cf-filter-apply");

    this.openBtn?.addEventListener("click", () => this.drawerOpen());
    this.closeBtn?.addEventListener("click", () => this.drawerClose());
    this.applyBtn?.addEventListener("click", () => this.drawerClose());
    this.backdrop?.addEventListener("click", () => this.drawerClose());

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.drawerIsOpen) this.drawerClose();
    });

    // Sort
    document.addEventListener("ab-sort:change", (e) => {
      this.sortVal = e.detail.value;

      document.querySelectorAll("ab-cf-sort").forEach((s) => {
        if (s !== e.target) s.silentSelect(e.detail.value);
      });

      const params = this.buildParams();

      e.detail.isDefault
        ? params.delete("sort_by")
        : params.set("sort_by", e.detail.value);

      params.delete("page");

      this.fetchUrl(buildUrl(this.baseUrl, params), true, false);
    });

    // Checkbox
    this.form.addEventListener("change", (e) => {
      if (e.target.type === "checkbox") this.fetchNow();
    });

    // Slider
    this.form.addEventListener("price-range:change", () => this.fetchNow());

    // Badge / clear
    document.addEventListener("click", (e) => {
      const badge = e.target.closest(".ab-cf-badge");

      if (badge) {
        e.stopPropagation();
        this.removeBadge(badge);
        return;
      }

      if (e.target.closest(".js-clear-filters")) this.clearAll();
    });

    // Pagination & Load More
    document.addEventListener("click", (e) => {
      const pageLink = e.target.closest(".ab-cf-pagination a");
      if (pageLink) {
        e.preventDefault();
        this.fetchUrl(pageLink.href, true, true, false);
      }

      const loadMoreLink = e.target.closest(".ab-cf-load-more-btn");
      if (loadMoreLink) {
        e.preventDefault();
        this.infiniteScrollEnabled = true;
        this.triggerLoadMore(loadMoreLink);
      }
    });

    // Grid toggle — sets data-cols on #ab-cf-product-grid
    // CSS reads data-cols via attribute selector to override .product-list columns
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".ab-cf-grid-toggle__btn");
      if (!btn) return;
      document
        .querySelectorAll(".ab-cf-grid-toggle__btn")
        .forEach((b) => b.setAttribute("aria-pressed", String(b === btn)));
      const grid = document.getElementById("ab-cf-product-grid");
      if (grid) grid.dataset.cols = btn.dataset.cols;
    });

    // Back button
    window.addEventListener("popstate", () => {
      this.sortVal =
        new URLSearchParams(window.location.search).get("sort_by") || "";

      document
        .querySelectorAll("ab-cf-sort")
        .forEach((s) => s.silentSelect(this.sortVal));

      this.fetchUrl(window.location.href, false, false);
    });

    this.updateClearBtn();

    // Mobile: default grid to 2 cols on load (toggle only has 2+1)
    if (window.matchMedia("(max-width: 768px)").matches) {
      const grid = document.getElementById("ab-cf-product-grid");
      if (grid) grid.dataset.cols = "2";
      document.querySelectorAll(".ab-cf-grid-toggle__btn").forEach((b) => {
        b.setAttribute("aria-pressed", String(b.dataset.cols === "2"));
      });
    }
  }

  // ── Drawer ────────────────────────────────────────────────────────

  get drawerIsOpen() {
    return this.sidebar?.classList.contains("is-open") ?? false;
  }

  drawerOpen() {
    this.sidebar?.classList.add("is-open");
    this.sidebar?.removeAttribute("aria-hidden");
    this.backdrop?.classList.add("is-visible");
    this.openBtn?.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";
    setTimeout(
      () => this.sidebar?.querySelector('button, [tabindex="0"]')?.focus(),
      50,
    );
  }

  drawerClose() {
    this.sidebar?.classList.remove("is-open");
    this.sidebar?.setAttribute("aria-hidden", "true");
    this.backdrop?.classList.remove("is-visible");
    this.openBtn?.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
    this.openBtn?.focus();
  }

  drawerUpdateCount(n) {
    const badge = document.getElementById("ab-cf-mobile-filter-count");
    if (badge) {
      badge.textContent = String(n);
      badge.hidden = n === 0;
    }
  }

  // ── Params ────────────────────────────────────────────────────────

  buildParams() {
    const params = new URLSearchParams();

    for (const [k, v] of new FormData(this.form)) {
      if (v) params.append(k, v);
    }

    return params;
  }

  doFetch() {
    const params = this.buildParams();

    if (this.sortVal) params.set("sort_by", this.sortVal);

    params.delete("page");

    this.fetchUrl(buildUrl(this.baseUrl, params), true, false);
  }

  triggerLoadMore(link) {
    if (this.isLoading) return;
    link.classList.add("is-loading");
    this.fetchUrl(link.dataset.url, false, false, true);
  }

  // ── Fetch ────────────────────────────────────────────────────────

  async fetchUrl(url, push = true, scroll = false, append = false) {
    this.isLoading = true;
    if (!append) {
      this.setLoading(true);
      this.infiniteScrollEnabled = false;
    }

    const cleanUrl = url
      .replace(/([?&])page=1(&|$)/, (_, pre, post) => (post ? pre : ""))
      .replace(/[?&]$/, "");

    const sectionId =
      document.querySelector("[data-section-id]")?.dataset.sectionId;

    try {
      const sep = cleanUrl.includes("?") ? "&" : "?";

      const fetchUrl = sectionId
        ? `${cleanUrl}${sep}sections=${sectionId}`
        : cleanUrl;

      const res = await fetch(fetchUrl, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!res.ok) throw new Error();

      const html = sectionId ? (await res.json())[sectionId] : await res.text();

      if (html) this.swap(html, append);

      if (push) history.pushState({}, "", cleanUrl);

      if ((scroll || push) && !append) this.scrollToSection();
    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading = false;
      if (!append) this.setLoading(false);

      requestAnimationFrame(() => {
        this.updateClearBtn();
      });
    }
  }

  // ── Dom SWAP ────────────────────────────────────────────────────────
  swap(rawHtml, append = false) {
    const doc = new DOMParser().parseFromString(rawHtml, "text/html");

    // ✅ FIX: update product grid
    const freshGrid = doc.querySelector("#ab-cf-product-grid");
    const liveGrid = document.getElementById("ab-cf-product-grid");

    if (freshGrid && liveGrid) {
      if (append) {
        const liveList = liveGrid.querySelector(".product-list");
        const freshList = freshGrid.querySelector(".product-list");

        if (liveList && freshList) {
          liveList.insertAdjacentHTML("beforeend", freshList.innerHTML);
        }

        const oldLoadMore = liveGrid.querySelector(
          ".ab-cf-load-more-container",
        );
        if (oldLoadMore) oldLoadMore.remove();

        const newLoadMore = freshGrid.querySelector(
          ".ab-cf-load-more-container",
        );
        if (newLoadMore) {
          liveGrid.appendChild(newLoadMore);
        }
      } else {
        const cols = liveGrid.dataset.cols; // preserve grid toggle
        liveGrid.innerHTML = freshGrid.innerHTML;
        if (cols) liveGrid.dataset.cols = cols;
      }
    }

    if (!append) {
      // product count
      this.replaceInner("#ab-cf-product-count", doc);

      // pagination
      this.replaceInner(".ab-cf-pagination", doc);
    }

    // other sync
    this.syncBadges(doc);
    this.syncPriceSlider(doc);
    this.syncCheckboxes(doc);
    this.syncGroupCounts(doc);

    requestAnimationFrame(() => {
      this.updateClearBtn();
      this.setupObserver();
    });
  }

  setupObserver() {
    if (!this.observer) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              this.infiniteScrollEnabled &&
              !this.isLoading
            ) {
              const loadMoreBtn = document.querySelector(
                ".ab-cf-load-more-btn",
              );
              if (loadMoreBtn) {
                this.triggerLoadMore(loadMoreBtn);
              }
            }
          });
        },
        { rootMargin: "0px" },
      );
    }

    this.observer.disconnect();
    const loadMoreBtn = document.querySelector(".ab-cf-load-more-btn");
    if (loadMoreBtn && this.infiniteScrollEnabled) {
      this.observer.observe(loadMoreBtn);
    }
  }

  replaceInner(sel, doc) {
    const fresh = doc.querySelector(sel);
    const live = document.querySelector(sel);
    if (fresh && live) live.innerHTML = fresh.innerHTML;
  }

  // ── Badges ────────────────────────────────────────────────────────

  syncBadges(doc) {
    const wrap = document.getElementById("ab-cf-active-filters");
    if (!wrap) return;
    const freshMap = new Map(
      [...doc.querySelectorAll("#ab-cf-active-filters .ab-cf-badge")].map(
        (b) => [this.badgeKey(b), b],
      ),
    );
    const liveMap = new Map(
      [...wrap.querySelectorAll(".ab-cf-badge")].map((b) => [
        this.badgeKey(b),
        b,
      ]),
    );
    for (const [k, b] of liveMap) {
      if (!freshMap.has(k)) b.remove();
    }
    for (const [k, b] of freshMap) {
      if (!liveMap.has(k)) wrap.appendChild(b.cloneNode(true));
    }
    // Show/hide the badges container
    wrap.hidden = wrap.querySelectorAll(".ab-cf-badge").length === 0;
    this.drawerUpdateCount(wrap.querySelectorAll(".ab-cf-badge").length);
  }

  badgeKey(b) {
    return b.dataset.filterType === "price_range"
      ? `price:${b.textContent.trim()}`
      : `list:${b.dataset.param}:${b.dataset.value}`;
  }

  // ── Price SYNC ────────────────────────────────────────────────────────

  syncPriceSlider(doc) {
    const fs = doc.querySelector("ab-filter-price-range");
    const ls = document.querySelector("ab-filter-price-range");

    if (fs && ls) {
      ls.updateRange(parseFloat(fs.dataset.min), parseFloat(fs.dataset.max));
    }
  }

  // ── Checkbox ────────────────────────────────────────────────────────

  syncCheckboxes(doc) {
    doc.querySelectorAll(".ab-filter-list__input").forEach((fc) => {
      const lc = this.form.querySelector(
        `.ab-filter-list__input[name="${CSS.escape(fc.name)}"][value="${CSS.escape(fc.value)}"]`,
      );
      if (!lc) return;
      const fCount = fc
        .closest(".ab-filter-list__label")
        ?.querySelector(".ab-filter-list__count");
      const lCount = lc
        .closest(".ab-filter-list__label")
        ?.querySelector(".ab-filter-list__count");
      if (fCount && lCount) lCount.textContent = fCount.textContent;
      lc.disabled = fc.disabled;
      lc.closest(".ab-filter-list__label")?.classList.toggle(
        "is-disabled",
        fc.disabled,
      );
    });
  }

  syncGroupCounts(doc) {
    const freshGroups = [...doc.querySelectorAll("ab-filter-group")];
    const liveGroups = [...document.querySelectorAll("ab-filter-group")];
    freshGroups.forEach((fg, i) => {
      const lg = liveGroups[i];
      if (!lg) return;
      const fb = fg.querySelector(".ab-filter-group__count");
      const lb = lg.querySelector(".ab-filter-group__count");
      if (fb && lb) lb.textContent = fb.textContent;
      else if (fb && !lb)
        lg.querySelector(".ab-filter-group__toggle")?.insertBefore(
          fb.cloneNode(true),
          lg.querySelector(".ab-filter-group__chevron"),
        );
      else if (!fb && lb) lb.remove();
    });
  }

  // ── Actions ────────────────────────────────────────────────────────

  removeBadge(badge) {
    if (badge.dataset.filterType === "price_range") {
      const slider = document.querySelector("ab-filter-price-range");
      slider?.reset();
      requestAnimationFrame(() => slider?.render());
    } else {
      const cb = this.form.querySelector(
        `.ab-filter-list__input[name="${badge.dataset.param}"][value="${badge.dataset.value}"]`,
      );
      if (cb) cb.checked = false;
    }

    this.fetchNow();
  }

  clearAll() {
    this.form
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach((cb) => (cb.checked = false));

    document.querySelector("ab-filter-price-range")?.reset();

    this.fetchNow();
  }

  // ── CLear Button ────────────────────────────────────────────────────────

  updateClearBtn() {
    const anyChecked = !!this.form?.querySelector(
      'input[type="checkbox"]:checked',
    );

    const slider = document.querySelector("ab-filter-price-range");

    const priceChanged = slider ? slider.userChanged : false;

    document.querySelectorAll(".js-clear-filters").forEach((btn) => {
      btn.hidden = !(anyChecked || priceChanged);
    });
  }

  // ─────────────────────────────────────

  setLoading(on) {
    document.getElementById("ab-cf-main")?.classList.toggle("is-loading", on);
  }

  scrollToSection() {
    const el = document.querySelector(".ab-cf-section");
    if (!el) return;

    window.scrollTo({
      top: el.offsetTop - 200,
      behavior: "smooth",
    });
  }
}

customElements.define("ab-cf-filter-form", AbCfFilterForm);
